// RadicalFractionToRationalPowerTask.js
// √[q](1/n) → a^(-p/q), gdzie n = a^p (n JEST policzone w treści)
// Podstawa w poleceniu podana matematycznie (KaTeX).
// Akceptujemy też formy 1/(a^(p/q)), 1/a^(p/q), a^-k (jeśli p/q się skróci do całkowitej), 1/a^k itd.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);

// ------- pomocnicze skracanie ułamków p/q -------

const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a || 1;
};

const reduceFraction = (p, q) => {
  const g = gcd(p, q);
  return { p: p / g, q: q / g };
};

// --- parser odpowiedzi (wzbogacony o warianty z 1/...) ---
function parseRationalPowerExtended(raw) {
  if (!raw) return null;
  const s = String(raw).replace(/\s/g, "");

  // 1/(a^(p/q))
  let m = s.match(/^1\/\((\d+)\^\((-?\d+)\/(\d+)\)\)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const p = -parseInt(m[2], 10);
    const q = parseInt(m[3], 10);
    return { base, p, q };
  }

  // 1/a^(p/q)
  m = s.match(/^1\/(\d+)\^\((-?\d+)\/(\d+)\)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const p = -parseInt(m[2], 10);
    const q = parseInt(m[3], 10);
    return { base, p, q };
  }

  // 1/a^k
  m = s.match(/^1\/(\d+)\^(\d+)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const p = -parseInt(m[2], 10);
    return { base, p, q: 1 };
  }

  // 1/(a^k)
  m = s.match(/^1\/\((\d+)\^(\d+)\)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const p = -parseInt(m[2], 10);
    return { base, p, q: 1 };
  }

  // a^(p/q)  lub a^(-p/q)
  m = s.match(/^(\d+)\^\((-?\d+)\/(\d+)\)$/);
  if (m) return { base: parseInt(m[1], 10), p: parseInt(m[2], 10), q: parseInt(m[3], 10) };

  // a^p/q  lub  a^-p/q (bez nawiasów)
  m = s.match(/^(\d+)\^\-?(\d+)\/(\d+)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const sign = s.includes("^-") ? -1 : 1;
    const p = sign * parseInt(m[2], 10);
    const q = parseInt(m[3], 10);
    return { base, p, q };
  }

  // a^-k  /  a^k
  m = s.match(/^(\d+)\^\-?(\d+)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const sign = s.includes("^-") ? -1 : 1;
    const p = sign * parseInt(m[2], 10);
    return { base, p, q: 1 };
  }

  return null;
}

function prettyPowerLatex(raw) {
  if (!raw) return "";
  const s = String(raw).replace(/\s/g, "");

  // 1/(a^(p/q))  |  1/a^(p/q)
  let m = s.match(/^1\/\((\d+)\^\((-?\d+)\/(\d+)\)\)$/);
  if (m) return `\\dfrac{1}{${m[1]}^{\\tfrac{${m[2]}}{${m[3]}}}}`;
  m = s.match(/^1\/(\d+)\^\((-?\d+)\/(\d+)\)$/);
  if (m) return `\\dfrac{1}{${m[1]}^{\\tfrac{${m[2]}}{${m[3]}}}}`;

  // 1/a^k  | 1/(a^k)
  m = s.match(/^1\/(\d+)\^(\d+)$/);
  if (m) return `\\dfrac{1}{${m[1]}^{${m[2]}}}`;
  m = s.match(/^1\/\((\d+)\^(\d+)\)$/);
  if (m) return `\\dfrac{1}{${m[1]}^{${m[2]}}}`;

  // a^(p/q)
  m = s.match(/^(\d+)\^\((-?\d+)\/(\d+)\)$/);
  if (m) return `${m[1]}^{\\tfrac{${m[2]}}{${m[3]}}}`;

  // a^p/q  | a^-p/q
  m = s.match(/^(\d+)\^\-?(\d+)\/(\d+)$/);
  if (m) {
    const sign = s.includes("^-") ? "-" : "";
    return `${m[1]}^{\\tfrac{${sign}${m[2]}}{${m[3]}}}`;
  }

  // a^-k | a^k
  m = s.match(/^(\d+)\^\-?(\d+)$/);
  if (m) {
    const sign = s.includes("^-") ? "-" : "";
    return `${m[1]}^{${sign}${m[2]}}`;
  }

  return s;
}

// --- generator ---
export function generateTask() {
  const base = pick([2, 3, 4, 5, 6, 7, 8, 9]);
  const q = pick([2, 3, 4, 5]); // stopień pierwiastka
  const pMax = base === 2 ? 6 : base === 3 ? 4 : 3;

  // p nie jest wielokrotnością q (żeby nie wyszło całkowite)
  const candidates = range(1, pMax).filter((x) => x % q !== 0);
  const p = pick(candidates.length ? candidates : [1]);

  const n = Math.pow(base, p);
  const leftLatex =
    q === 2 ? `\\sqrt{\\tfrac{1}{${n}}}` : `\\sqrt[${q}]{\\tfrac{1}{${n}}}`;

  return {
    type: "RadicalFractionToRationalPowerTask",
    data: { base, p, q, n, leftLatex }
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ marginBottom: "12px" }}>
      Zapisz w postaci potęgi o podstawie <InlineMath math={`${data.base}`} />:{" "}
      <InlineMath math={data.leftLatex} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const handleChange = (ans) => onChange?.(ans);
  const { base, p, q } = task.data;
  const ph = `np. ${base}^(-${p}/${q}) lub 1/${base}^(${p}/${q})`;

  return (
    <div>
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        placeholder={"np. 5^2/3 lub 5^-2/3"}
        label=""
        powerPreview={true}
      />
      <div aria-hidden style={{ marginTop: "-16px" }} />
      <div aria-hidden style={{ height: "12px" }} />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return false;
  const parsed = parseRationalPowerExtended(userAnswer.raw);
  if (!parsed) return false;

  const { base, p, q } = data;
  if (parsed.base !== base) return false;

  // Sprawdzamy równoważność: oczekiwany wykładnik = -(p/q)
  return parsed.p * q === -p * parsed.q;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return <i>(brak odpowiedzi)</i>;

  return <InlineMath math={prettyPowerLatex(userAnswer.raw)} />;
}

// --- odpowiedź kanoniczna: minus na zewnątrz i skrócony ułamek ---
export function renderAnswer({ data }) {
  const { base, p, q } = data;
  const { p: pr, q: qr } = reduceFraction(p, q);
  // zawsze zapisujemy jako  base^{- p'/q' }
  return <InlineMath math={`${base}^{-\\tfrac{${pr}}{${qr}}}`} />;
}

// --- wyjaśnienie z dodatkowym krokiem skracania, jeśli trzeba ---
export function renderExplanation({ data }) {
  const { base, p, q, n } = data;

  const factor =
    p > 1
      ? `\\tfrac{1}{${n}}\\;=\\;\\tfrac{1}{${base}^{${p}}}\\;=\\;${base}^{-${p}}`
      : `\\tfrac{1}{${n}}\\;=\\;\\tfrac{1}{${base}}\\;=\\;${base}^{-1}`;

  const def =
    "a^{\\tfrac{p}{q}}=\\sqrt[q]{\\,a^{p}\\,}\\quad (a>0,\\; q\\in\\mathbb{N}_{\\ge 2})";

  const rootPart =
    q === 2
      ? `\\sqrt{\\tfrac{1}{${n}}}`
      : `\\sqrt[${q}]{\\tfrac{1}{${n}}}`;
  const innerPow = q === 2 ? `\\sqrt{${base}^{-${p}}}` : `\\sqrt[${q}]{${base}^{-${p}}}`;

  const { p: pr, q: qr } = reduceFraction(p, q);
  const fracOrig = `-\\tfrac{${p}}{${q}}`;
  const fracRed = `-\\tfrac{${pr}}{${qr}}`;

  let powerLine = `${rootPart}\\;=\\;${innerPow}\\;=\\;${base}^{${fracOrig}}`;

  // jeśli da się skrócić, dokładamy jeszcze jedno (lub dwa) " = "
  if (pr !== p || qr !== q) {
    powerLine += `\\;=\\;${base}^{${fracRed}}`;
    if (pr === qr) {
      // np. -2/2 -> -1
      powerLine += `\\;=\\;${base}^{-1}`;
    }
  }

  return (
    <div style={{ display: "grid", rowGap: "8px" }}>
      <p>
        <strong>Krok 1:</strong> Zauważ, że liczba pod pierwiastkiem jest
        mniejsza od 1.
      </p>
      <BlockMath math={factor} />
      <p>
        <strong>Krok 2:</strong> Skorzystaj z definicji wykładnika wymiernego.
      </p>
      <BlockMath math={def} />
      <p>
        <strong>Krok 3:</strong> Zapisz pierwiastek w postaci potęgi o wskazanej
        podstawie.
      </p>
      <BlockMath math={powerLine} />
    </div>
  );
}
